import { useEffect } from 'react'
import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'

const ProgressBar = styled.div<{ fromBottom?: number }>`
    background-color: ${theme.colors.businessBlue};
    height: 5px;
    width: 0%;
    position: fixed;
    padding: 0 !important;
    z-index: ${theme.zIndex.modal};
    ${({ fromBottom }) =>
        fromBottom || fromBottom === 0
            ? css`
                  bottom: ${fromBottom}px;
              `
            : css`
                  inset: 0;
                  @media (max-width: 750px) {
                      display: none;
                  }
              `};
`

export const ArticleProgress = (o?: { fromBottom?: number }) => {
    useEffect(() => {
        const progress = document.getElementById('progress') as HTMLDivElement

        if (!progress) return

        document.addEventListener(
            'scroll',
            () => {
                const scroll = window.scrollY
                const height = document.documentElement.scrollHeight - window.innerHeight
                const progressWidth = Math.floor((100 * scroll) / height)
                progress.style.width = `${progressWidth}%`
            },

            { passive: true }
        )
    }, [])

    return <ProgressBar id="progress" fromBottom={o?.fromBottom} className="article-progress" />
}

export const ContentWrapper = styled.article<{
    makeTextBolder?: boolean
    isDarkTheme?: boolean
    showGradientKnowPageDesign?: boolean
}>`
    min-height: inherit;
    width: 100%;
    display: grid;
    gap: 10px;
    section {
        all: unset;
    }
    div {
        padding: 7.5px 0;
    }
    div {
        text-align: justify;
        font-family: var(--font-inter);
        font-weight: 400;
    }
    h1,
    h2 {
        font-family: var(--font-inter);
        font-size: 24px;
        font-weight: 600;
        line-height: 32px;
        color: ${theme.colors.deepSeaBlue};
        margin: 0;
        text-align: left !important;
    }
    h2 {
        padding-top: 20px;
        line-height: 42px;
        color: ${theme.colors.headlineDark};
    }
    h3 {
        text-transform: none;
        font-family: var(--font-inter);
        font-size: 20px;
        font-weight: 600;
        line-height: 30px;
        color: ${theme.colors.subtitleBlack};
        margin-bottom: 0px;
    }
    h4 {
        text-transform: none;
        font-family: var(--font-inter);
        font-size: 18px;
        font-weight: 600;
        line-height: 26px;
        color: ${theme.colors.deepSeaBlue};
        margin-bottom: 0px;
    }
    ol,
    ul,
    div {
        margin: 0;
        display: grid;
        gap: 0px;
        li {
            margin: 0;
        }
    }

    pre {
        margin: 0;
    }
    code,
    p,
    a,
    span,
    li {
        font-family: var(--font-open-sans);
        font-size: 16px !important;
        font-weight: 400;
        font-display: swap;
        line-height: 32px;
        color: ${({ makeTextBolder }) => (makeTextBolder ? theme.colors.black : theme.colors.body1Black)};
        margin: 0;
    }
    a {
        color: ${theme.colors.deepSeaBlue};
    }
    b,
    strong {
        font-weight: 600;
        color: inherit;
        font-size: inherit;
        line-height: inherit;
        font-family: inherit;
    }
    .promo {
        display: block;
        cursor: pointer;
        display: grid;
        grid-template-columns: 140px 1fr;
        gap: 50px;
        padding: 30px;
        min-width: 55vw;
        height: fit-content;
        background: #e7f6ec;
        border-radius: ${theme.borderRadius.normal};
        @media (max-width: 1050px) {
            height: auto;
            justify-content: center;
            svg {
                display: none;
            }
            grid-template-columns: 1fr;
        }
        &:hover {
            transition: background-color 0.35s linear;
            will-change: background-color;
            background-color: #2fa55e;
            * {
                transition: background-color 0.35s linear;
                color: ${theme.colors.white};
            }
            svg {
                rect {
                    fill: #2fa55e;
                }
            }
            button {
                transition: background-color 0.35s linear;
                background-color: ${theme.colors.white};
                p {
                    color: #2fa55e;
                }
            }
        }

        h3 {
            margin: 0;
            font-size: 24px;
            font-weight: 700;
            line-height: 30px;
            color: ${theme.colors.headlineDark};
        }

        .texts {
            display: grid;
            gap: 10px;
            p {
                font-size: 16px;
                font-weight: 500;
            }
            @media (max-width: 750px) {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 20px;
                * {
                    text-align: center;
                }
                button {
                    height: 30px;
                    width: 100px;
                }
                height: auto;
            }
        }
        button {
            all: unset;
            cursor: pointer;
            width: fit-content;
            padding: 10px 25px;
            border-radius: ${theme.borderRadius.normal};
            background-color: #2fa55e;
            p {
                font-weight: 600 !important;
                color: ${theme.colors.white};
            }
        }
    }
    blockquote {
        padding: 15px;
        margin: 0;
        background-color: ${() => {
            switch (Math.floor(Math.random() * 3) + 1) {
                case 1:
                    return theme.colors.primitiveGreen
                case 2:
                    return theme.colors.primitiveOrange
                case 3:
                    return `${theme.colors.snowWhite}99`
                default:
                    return `${theme.colors.snowWhite}99`
            }
        }};
        border-radius: ${theme.borderRadius.normal};
    }
    * {
        word-break: break-word;
    }
    code {
        white-space: normal;
    }

    blockquote {
        padding: 15px;
        margin: 0;
        border: ${() => {
            switch (Math.floor(Math.random() * 3) + 1) {
                case 1:
                    return `2px dashed ${theme.colors.primitiveGreen}`
                case 2:
                    return `2px dashed ${theme.colors.primitiveOrange}`
                case 3:
                default:
                    return `2px dashed ${theme.colors.snowWhite}`
            }
        }};
        border-radius: ${theme.borderRadius.normal};
    }

    ${({ isDarkTheme }) =>
        isDarkTheme
            ? css`
                  blockquote {
                      background-color: ${theme.colors.darkThemePrimary};
                  }
                  * {
                      color: white !important;
                  }
                  a {
                      color: ${theme.colors.plusPlanYellow} !important;
                      font-weight: 800;

                      &:hover {
                          color: ${theme.colors.lightAccentBorder} !important;
                          text-decoration: none;
                      }
                  }
              `
            : ''}

    ${({ showGradientKnowPageDesign }) =>
        showGradientKnowPageDesign
            ? css`
                  blockquote {
                      background-color: ${theme.colors.backgroundTransparent};
                      border: none;
                  }
              `
            : ''}
    .katex .base {
        max-width: 100% !important;
    }
    span.mfrac > span.vlist-t > span.vlist-r > span.vlist > span:nth-child(1) {
        position: relative;
        top: -32.5px !important;
    }
`
